import { useState, useEffect } from 'react';
import { useBulkProcessDayLogsMutation } from 'redux/features/timeLogApi';
import { toast } from 'react-toastify';
import useUser from 'hooks/useUser';
import dayjs from 'lib/dayjs';

import { getAllSundays } from 'utils/common';
import MonthYearNavigation from 'components/ui/MonthYearNavigation';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';

import {
  formatTime,
  formatDate,
  formatHoursMinutes,
  formatTimeFromDecimal,
} from '../../../utils/timeTracker';
import Icon from '../../../components/AppIcon';
import DayLogEditModal from './DayLogEditModal';

const MonthlyLogsTable = ({ monthlyLogs, summary, onMonthChange }) => {
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth() + 1);
  const [selectedDayLogs, setSelectedDayLogs] = useState([]);
  const [bulkProcessDayLogs] = useBulkProcessDayLogsMutation();
  const [dayEditModalOpen, setDayEditModalOpen] = useState(false);
  const [selectedDayToEdit, setSelectedDayToEdit] = useState(null);
  const [settings, setSettings] = useState();
  const user = useUser();

  const { data: settingsData } = useGetSettingsQuery();

  useEffect(() => {
    if (settingsData?.data) {
      setSettings(settingsData?.data);
    }
  }, [settingsData]);

  // Keep selectedDayLogs in sync with monthlyLogs updates (e.g. after a submission)
  useEffect(() => {
    if (dayEditModalOpen && selectedDayToEdit) {
      const currentDay = monthlyLogs?.find((log) => isSameDate(log.date, selectedDayToEdit));
      if (currentDay) {
        setSelectedDayLogs(currentDay.fullDayLogs || []);
      }
    }
  }, [monthlyLogs, dayEditModalOpen, selectedDayToEdit]);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ].filter((month) => {
    if (selectedYear === currentYear) {
      return month.value <= currentMonth;
    }
    return selectedYear < currentYear;
  });

  useEffect(() => {
    if (selectedYear === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
      onMonthChange?.(selectedYear, currentMonth);
    }
  }, [selectedYear, selectedMonth, currentYear, currentMonth, onMonthChange]);

  const totalMonthlyHours = summary
    ? summary.totalMonthlyHours
    : monthlyLogs?.reduce((sum, log) => sum + (log?.totalHours || 0), 0) || 0;
  const totalOvertimeHours = summary
    ? summary.totalOvertimeHours
    : monthlyLogs?.reduce((sum, log) => sum + (log?.overtime || 0), 0) || 0;

  const getCompanyEventIcon = (eventType) => {
    switch (eventType) {
      case 'Event Celebration':
        return '🎉';
      case 'Full Holiday':
        return '🏢';
      case 'Half Holiday':
        return '⏰';
      case 'Saturday Off':
        return '🏠';
      case 'FULL_DAY_LEAVE':
        return '🚢';
      case 'HALF_DAY_LEAVE':
        return '🌓';
      case 'SHORT_LEAVE':
        return '⏱️';
      case 'LONG_TIME_LEAVE':
        return '📅';
      case 'MULTIPLE_DAY_LEAVE':
        return '🗓️';
      default:
        return '📅';
    }
  };

  const getThisMonthHoliDayCount = () => {
    const total = monthlyLogs?.reduce((sum, log) => {
      if (log?.holidayType === 'Full_Holiday' || log?.holidayType === 'Saturday_Off') {
        return sum + 1;
      }
      if (log?.holidayType === 'Half_Holiday') {
        return sum + 0.5;
      }
      return sum;
    }, 0);
    const sundays = getAllSundays(selectedYear, selectedMonth);
    return total + sundays.length;
  };

  const getEventsForDate = (log) => {
    if (log.holiday) {
      return {
        icon: getCompanyEventIcon(log.holidayType),
        label: log.holidayTitle,
        type: 'holiday',
      };
    }
    if (log.leaveType) {
      return {
        icon: getCompanyEventIcon(log.leaveType),
        label: log.leaveType?.replace(/_/g, ' '),
        duration: log.leaveDuration,
        type: 'leave',
      };
    }
    return null;
  };

  const handleBulkDayUpdate = async (payload) => {
    try {
      await bulkProcessDayLogs({ userId: user?.id, ...payload }).unwrap();
      toast.success(
        user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
          ? 'Logs updated successfully'
          : 'Log update requests submitted successfully',
      );
    } catch {
      // Error toast is handled by baseQueryV1 for API errors
    }
  };

  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const isSameDate = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const isFcAllow = settings?.is_fc_allow;
  const gridTemplate = user?.overtime_enabled
    ? 'grid-cols-[2.2fr_1fr_1.3fr_1.8fr_1.2fr_1fr_2fr]'
    : 'grid-cols-[2.2fr_1fr_1.3fr_1.8fr_1.2fr_2fr]';

  return (
    <div className="bg-surface border border-border rounded-xl p-4 sm:p-6 shadow-sm ">
      {/* Header: Title + controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-text-primary flex items-center space-x-2">
            <Icon name="BarChart3" size={20} />
            <span>Monthly Time Logs</span>
          </h2>
          <p className="text-xs sm:text-sm text-text-secondary mt-1">
            {months?.find((m) => m?.value === selectedMonth)?.label} {selectedYear}
          </p>
        </div>

        {/* Month/Year Selector with Navigation */}
        <MonthYearNavigation
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          setSelectedMonth={setSelectedMonth}
          setSelectedYear={setSelectedYear}
          onMonthChange={onMonthChange}
        />
      </div>

      {/* Summary cards */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${user?.overtime_enabled ? 4 : 3} gap-3 sm:gap-4 mb-4 sm:mb-6`}
      >
        <div className="p-3 sm:p-4 bg-primary-50 rounded-lg border border-primary-100/60">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Calendar" size={16} color="var(--color-primary)" />
            <p className="text-xs sm:text-sm text-primary-700 font-medium">Total Days</p>
          </div>
          <p className="text-base sm:text-lg font-semibold text-primary-900">
            {summary ? summary.totalDays : monthlyLogs?.length || 0} days
          </p>
        </div>

        <div className="p-3 sm:p-4 bg-success-50 rounded-lg border border-success-100/60">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} color="var(--color-success)" />
            <p className="text-xs sm:text-sm text-success-700 font-medium">Total time</p>
          </div>
          <p className="text-base sm:text-lg font-semibold text-success-900">
            {formatHoursMinutes(totalMonthlyHours)}
          </p>
        </div>

        <div className="p-3 sm:p-4 bg-warning-50 rounded-lg sm:col-span-2 lg:col-span-1 border border-warning-100/60">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={16} color="var(--color-warning)" />
            <p className="text-xs sm:text-sm text-warning-700 font-medium">Avg Per Day</p>
          </div>
          <p className="text-base sm:text-lg font-semibold text-warning-900">
            {formatHoursMinutes(
              summary
                ? summary.avgWorkingHours
                : monthlyLogs?.length > 0
                  ? totalMonthlyHours / (monthlyLogs?.length - getThisMonthHoliDayCount())
                  : 0,
            )}
          </p>
        </div>
        {user?.overtime_enabled && (
          <div className="p-3 sm:p-4 bg-purple-50 rounded-lg border border-purple-100/60">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Timer" size={16} color="rgb(147, 51, 234)" />
              <p className="text-xs sm:text-sm text-purple-700 font-medium">Overtime</p>
            </div>
            <p className="text-base sm:text-lg font-semibold text-purple-900">
              {formatHoursMinutes(totalOvertimeHours)}
            </p>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-6 sticky top-0 z-10">
          <div className={`grid ${gridTemplate} gap-4 items-center`}>
            {[
              'Date',
              'In',
              'Out',
              'Working Hours',
              'Total Hours',
              ...(user?.overtime_enabled ? ['Overtime'] : []),
              'Event/Leave',
            ].map((header) => (
              <h3
                key={header}
                className="text-[12px] font-bold text-slate-500 uppercase tracking-[0.1em]"
              >
                {header}
              </h3>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="divide-y divide-border max-h-96 overflow-y-auto bg-background">
          {!monthlyLogs || monthlyLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 border border-slate-100">
                <Icon name="FileX" size={28} className="text-slate-300" />
              </div>
              <p className="text-sm font-bold text-slate-900 uppercase tracking-widest">
                No Records Found
              </p>
              <p className="text-xs text-slate-400 mt-1 font-medium">
                No activity logs available for{' '}
                {months?.find((m) => m?.value === selectedMonth)?.label}, {selectedYear}
              </p>
            </div>
          ) : (
            monthlyLogs.map((log) => {
              const rowOpen = expandedRow === log.date;
              const isForceCheckout = log?.forceCheckout;
              const isToday = isSameDate(log.date, currentDate);
              const weekday = new Date(log.date).getDay(); // 0 = Sun, 6 = Sat
              const isWeekend = weekday === 0;
              const eventInfo = getEventsForDate(log);

              return (
                <div
                  key={log.date}
                  className={`
                    group transition-all duration-200
                    ${rowOpen ? 'bg-primary-50/30' : 'bg-white hover:bg-slate-50'}
                  `}
                >
                  {/* Main row */}

                  <div
                    className={`
                      px-4 py-3.5 grid ${gridTemplate}
                      gap-4 items-center cursor-pointer transition-colors
                      ${isForceCheckout ? 'bg-red-50/50' : ''}
                    `}
                    onClick={() => toggleRow(log.date)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          className={`
                              flex justify-center items-center w-8 h-8 rounded-full 
                              border transition-all duration-200
                              ${rowOpen ? 'bg-white border-primary-200 shadow-sm text-primary-600' : 'bg-slate-50 border-slate-200 text-slate-400 group-hover:border-slate-300 group-hover:text-slate-600'}
                            `}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(log.date);
                          }}
                        >
                          <Icon size={16} name={rowOpen ? 'ChevronUp' : 'ChevronDown'} />
                        </button>
                      </div>

                      <div>
                        <p
                          className={`text-xs sm:text-sm font-medium ${
                            isToday ? 'text-primary-700' : 'text-text-primary'
                          }`}
                        >
                          {formatDate(log.date)}
                          {log.hasPendingRequest && (
                            <span
                              className="ml-2 inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                              title="Pending updates"
                            />
                          )}
                          {isToday && (
                            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 font-semibold">
                              Today
                            </span>
                          )}
                        </p>
                        <p
                          className={`text-[11px] text-text-secondary ${isWeekend ? 'italic' : ''}`}
                        >
                          {new Date(log.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                          })}
                          {isWeekend && ' • Weekend'}
                          {log.hasPendingRequest && (
                            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">
                              Pending
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* IN */}
                    <div className="text-sm font-medium text-slate-600">
                      {log.firstCheckIn ? formatTime(log.firstCheckIn) : '--:--'}
                    </div>

                    {/* OUT */}
                    <div className="text-sm font-medium text-slate-600">
                      {!log.lastCheckOut || log.forceCheckout
                        ? '--:--'
                        : formatTime(log.lastCheckOut)}
                    </div>
                    <div className="flex">
                      <span className="inline-flex w-[80px] justify-center  items-center px-2 py-1 rounded-md bg-slate-50 text-slate-500 text-[13px] font-bold border border-slate-100  tracking-tight">
                        {log?.working_hours > 0 ? formatHoursMinutes(log.working_hours) : '--'}
                      </span>
                    </div>

                    {/* TIME */}
                    <div className="flex">
                      {log.totalHours ? (
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-lg inline-flex justify-center min-w-[70px] ${
                            log?.totalHours >= (log?.isHalfdayHoliday ? 4.5 : 9)
                              ? 'text-emerald-700 bg-emerald-50 border border-emerald-100'
                              : log?.totalHours >= (log?.isHalfdayHoliday ? 3.5 : 7)
                                ? 'text-amber-700 bg-amber-50 border border-amber-100'
                                : 'text-rose-700 bg-rose-50 border border-rose-100'
                          } `}
                        >
                          {formatHoursMinutes(log.totalHours || 0)}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-400 px-2.5 py-1 inline-flex justify-center min-w-[70px] bg-slate-50 border border-slate-100 rounded-lg">
                          --
                        </span>
                      )}
                    </div>

                    {/* OVERTIME */}
                    {user?.overtime_enabled && (
                      <div className="flex">
                        {log.overtime > 0 ? (
                          <span className="text-xs font-bold px-2.5 py-1 rounded-lg text-indigo-700 bg-indigo-50 border border-indigo-100 min-w-[70px] inline-flex justify-center">
                            {formatHoursMinutes(log.overtime)}
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-slate-400 px-2.5 py-1 inline-flex justify-center min-w-[70px] bg-slate-50 border border-slate-100 rounded-lg">
                            --
                          </span>
                        )}
                      </div>
                    )}

                    {/* EVENT/LEAVE */}
                    <div className="flex items-center">
                      {eventInfo ? (
                        <span
                          title={
                            eventInfo.type === 'leave'
                              ? `Duration: ${eventInfo.duration} ${eventInfo.duration > 1 ? 'days' : 'day'}`
                              : ''
                          }
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${eventInfo.type === 'leave' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-slate-50 text-slate-600 border-slate-100'} border`}
                        >
                          <span className="text-base leading-none translate-y-[1px]">
                            {eventInfo.icon}
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-tight truncate max-w-[120px]">
                            {eventInfo.label}
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </div>
                  </div>

                  {/* Expanded section */}
                  {rowOpen && (
                    <div className="bg-slate-50/50 px-6 py-6 border-t border-slate-100 animate-slideDown">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                          <Icon name="ClipboardList" size={18} className="text-slate-400" />
                          <p className="font-bold text-xs uppercase tracking-[0.15em] text-slate-500">
                            Log Session Details
                          </p>
                        </div>
                        {settings?.is_log_update_allow && !isToday && (
                          <button
                            disabled={isForceCheckout && !isFcAllow}
                            onClick={() => {
                              setSelectedDayToEdit(log.date);
                              setSelectedDayLogs(log.fullDayLogs || []);
                              setDayEditModalOpen(true);
                            }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl  transition-all text-[11px] font-bold uppercase tracking-wider shadow-sm ${
                              isForceCheckout && !isFcAllow
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-indigo-50 text-indigo-600 border-2 border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200'
                            }`}
                          >
                            <Icon name="Edit3" size={14} />
                            <span>Edit</span>
                          </button>
                        )}
                      </div>

                      {log?.fullDayLogs?.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-8 bg-white/50 rounded-xl border border-dashed border-slate-200">
                          <Icon name="Ghost" size={24} className="text-slate-300 mb-2" />
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">
                            No active sessions recorded
                          </p>
                        </div>
                      )}

                      {log?.fullDayLogs?.length > 0 && (
                        <div className="grid gap-4">
                          {log?.fullDayLogs
                            ?.filter((details) => details.id !== null)
                            .map((detail, index) => {
                              let breakBetween = '';
                              const nextLog = log?.fullDayLogs?.filter(
                                (details) => details.id !== null,
                              )[index + 1];
                              if (nextLog) {
                                const breakDuration = dayjs(nextLog.check_in).diff(
                                  dayjs(detail.check_out),
                                  'minutes',
                                );
                                breakBetween = formatTimeFromDecimal(breakDuration / 60);
                              }
                              return (
                                <div
                                  key={detail.id || detail._id || index}
                                  className="group/detail bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:border-primary-200 transition-all duration-200"
                                >
                                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 items-center">
                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Check In
                                      </p>
                                      <p className="text-sm font-bold text-slate-700">
                                        {detail.check_in ? formatTime(detail.check_in) : '--:--'}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Check Out
                                      </p>
                                      <p className="text-sm font-bold text-slate-700">
                                        {detail.check_out ? formatTime(detail.check_out) : '--:--'}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Duration
                                      </p>
                                      <p className="text-sm font-bold text-emerald-600">
                                        {formatHoursMinutes(detail.total_hours || 0)}
                                      </p>
                                    </div>

                                    <div className="lg:col-span-1">
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Reason
                                      </p>
                                      <p
                                        className="text-xs font-medium text-slate-600 truncate"
                                        title={detail.reason}
                                      >
                                        {detail.reason || 'Regular Session'}
                                        {breakBetween && (
                                          <span className="text-amber-500 ml-1 font-bold">
                                            ({breakBetween} Break)
                                          </span>
                                        )}
                                      </p>
                                    </div>

                                    {/* Individual Correct buttons removed in favor of bulk edit */}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {dayEditModalOpen && (
        <DayLogEditModal
          isOpen={dayEditModalOpen}
          onClose={() => {
            setDayEditModalOpen(false);
            setSelectedDayToEdit(null);
            setSelectedDayLogs([]);
          }}
          date={selectedDayToEdit}
          logs={selectedDayLogs}
          onSubmit={handleBulkDayUpdate}
          user={user}
        />
      )}
    </div>
  );
};

export default MonthlyLogsTable;
